import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Grid, Stepper, Step, StepLabel, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import SectionCard from '../../../components/SectionCard';
import InvButton from '../../../components/InvButton';
import { getStores } from '../../../api/stockLedger';
import { getPatientPrescriptionDetails } from '../../../api/pharmacy';
import { resolvePharmacyScope } from '../../../hooks/usePharmacyRole';
import useInvRole from '../../../hooks/useInvRole';
import { INV_ACTIONS } from '../../../utils/inventoryRoles';
import { formatDateTime, parseMoney } from '../../../utils';
import { formatDispensingError } from '../../../utils/dispensingEntity';
import { extractOpdGenericTerm, mapOpdPatientEntries, pickLookupFields } from '../../../utils/opdPrescriptionEntity';
import {
  STOCK_STATUS,
  enrichItemUom,
  fetchPrescribeSearch,
  resolvePrescribeLineStock,
} from '../../../utils/pharmacyAvailability';
import { IPD_COLORS } from '../../../theme';
import { fadeInSx } from '../pharmacyStyles';
import { SummaryStat, DetailSummaryGrid } from '../PharmacyUi';
import SubstitutePickerDialog from '../components/SubstitutePickerDialog';
import useDirectDispensePipeline from '../direct/useDirectDispensePipeline';
import DirectDispenseMedicineStep, {
  MEDICINE_LINE_DEFAULT,
  medicineLinesValid,
} from '../direct/DirectDispenseMedicineStep';
import PrescribedMedicinesTable from './PrescribedMedicinesTable';

const STEPS = ['Review prescription', 'Medicines & payment'];

/**
 * Catalogue items → dispense lines. PharmacyBatchPicker auto-selects the FEFO batch
 * from `medicine.batches`, so a seeded line opens ready to dispense. A trailing blank
 * row keeps the manual "add another medicine" path available.
 */
function seedLinesFromItems(items) {
  const seeded = items
    .filter((item) => item?.item_id)
    .map((item) => ({ ...MEDICINE_LINE_DEFAULT, medicine_id: item.item_id, medicine: item }));
  return [...seeded, { ...MEDICINE_LINE_DEFAULT }];
}

export default function OpdDispenseFlow() {
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const backUrl = `${url.replace(/\/dispense$/, '')}?mode=existing`;
  const pipeline = useDirectDispensePipeline();
  const { can } = useInvRole();

  // The lookup key and row id ride in the URL, so a refresh or a bookmark
  // re-resolves the prescription instead of dropping the pharmacist back to
  // the search — same shape as StartDispensePage's :prescriptionId.
  const params = new URLSearchParams(location.search);
  const lookupFields = pickLookupFields(Object.fromEntries(params));
  const lookupQuery = params.toString();
  const prescriptionId = params.get('rx') || '';
  const seededRx = location.state?.prescription;

  const [rx, setRx] = useState(null);
  const [rxLoading, setRxLoading] = useState(true);
  const [rxError, setRxError] = useState(null);

  const [step, setStep] = useState(0);
  const [lines, setLines] = useState([{ ...MEDICINE_LINE_DEFAULT }]);
  const [storeId, setStoreId] = useState('');
  const [store, setStore] = useState(null);
  const [storeError, setStoreError] = useState(null);

  /** Per prescribed medicine id: { loading, status, item, message }. */
  const [availability, setAvailability] = useState({});
  const [substituteDialog, setSubstituteDialog] = useState({
    open: false, medId: null, loading: false, items: [], prescribedMedicine: '', searchTerm: '',
  });

  useEffect(() => {
    let alive = true;
    getStores({ is_pharmacy_store: true })
      .then((data) => {
        if (!alive) return;
        const storeList = Array.isArray(data) ? data : (data?.items || []);
        const first = storeList[0];
        if (first) {
          setStoreId(first.store_id || first.id || '');
          setStore(first);
          setStoreError(null);
        } else {
          setStoreError('No pharmacy store is configured for this facility, so nothing can be dispensed.');
        }
      })
      .catch((e) => {
        // Surfaced now rather than at payment time, where it reads as a billing failure.
        if (alive) setStoreError(formatDispensingError(e, 'Could not load the pharmacy store. Reload and try again.'));
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    // The list already holds the row; it rides along in router state so the normal
    // click-through costs no extra request. The URL params are what make a refresh
    // or a bookmark work, and only then do we re-resolve.
    if (seededRx && seededRx.id === prescriptionId) {
      setRx(seededRx);
      setRxLoading(false);
      return undefined;
    }

    if (!Object.keys(lookupFields).length || !prescriptionId) {
      setRxLoading(false);
      setRxError('This dispense link is incomplete. Start again from Direct Dispenses.');
      return undefined;
    }

    let alive = true;
    setRxLoading(true);
    setRxError(null);

    getPatientPrescriptionDetails(lookupFields)
      .then(({ patients }) => {
        if (!alive) return;
        const row = mapOpdPatientEntries(patients)
          .flatMap((entry) => entry.rows)
          .find((r) => r.id === prescriptionId);
        if (row) setRx(row);
        else setRxError('That prescription is no longer available. Search again from Direct Dispenses.');
      })
      .catch((e) => {
        if (alive) setRxError(e?.message || 'Could not load the prescription.');
      })
      .finally(() => {
        if (alive) setRxLoading(false);
      });

    return () => { alive = false; };
    // lookupFields is rebuilt every render; lookupQuery (the same params, as a string) is its stable key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededRx, lookupQuery, prescriptionId]);

  // Brand-availability fan-out, one call per prescribed medicine, exactly as
  // Active Dispenses does. Keyed on the medicine ids and the ORIGINAL prescribed
  // names so applying a substitute cannot re-trigger and clobber its own result.
  const medications = useMemo(() => rx?.medications || [], [rx]);
  const medicineSignature = useMemo(
    () => medications.map((m) => `${m.id}:${m.name}`).join('|'),
    [medications],
  );
  const scope = useMemo(
    () => resolvePharmacyScope(store, {}),
    [store],
  );

  useEffect(() => {
    if (!storeId || !medications.length) {
      setAvailability({});
      return undefined;
    }

    let alive = true;
    setAvailability(Object.fromEntries(
      medications.map((m) => [m.id, { loading: true, status: null, item: null, message: null }]),
    ));

    Promise.all(medications.map((med) => resolvePrescribeLineStock({
      line: { id: med.id, medicine: med.name, route: med.route },
      tenantId: scope.tenant_id,
      hospitalId: scope.hospital_id,
      storeId,
    }))).then((results) => {
      if (!alive) return;
      setAvailability(Object.fromEntries(results.map((r) => [r.lineId, {
        loading: false, status: r.status, item: r.item, message: r.message,
      }])));
      // Seed the dispense lines with everything already in stock.
      const inStock = results.filter((r) => r.status === STOCK_STATUS.IN_STOCK && r.item);
      if (inStock.length) {
        setLines(seedLinesFromItems(inStock.map((r) => r.item)));
      }
    });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, medicineSignature, scope.tenant_id, scope.hospital_id]);

  async function openSubstitute(med) {
    const searchTerm = extractOpdGenericTerm(med.name);
    if (!storeId) {
      toast.error('No pharmacy store available');
      return;
    }
    setSubstituteDialog({
      open: true, medId: med.id, loading: true, items: [],
      prescribedMedicine: med.name, searchTerm,
    });
    const items = await fetchPrescribeSearch({
      tenant_id: scope.tenant_id,
      hospital_id: scope.hospital_id,
      location_id: storeId,
      q: searchTerm,
    });
    setSubstituteDialog((prev) => (prev.medId === med.id ? { ...prev, loading: false, items } : prev));
  }

  function applySubstitute(medId, substitute) {
    enrichItemUom(substitute).then((enriched) => {
      setAvailability((prev) => ({
        ...prev,
        [medId]: { loading: false, status: STOCK_STATUS.IN_STOCK, item: enriched, message: null },
      }));
      // Append, never rebuild — rebuilding would discard batch picks already made
      // on the other rows.
      setLines((prev) => {
        if (prev.some((l) => l.medicine_id === enriched.item_id)) return prev;
        return [
          ...prev.filter((l) => l.medicine_id),
          { ...MEDICINE_LINE_DEFAULT, medicine_id: enriched.item_id, medicine: enriched },
          { ...MEDICINE_LINE_DEFAULT },
        ];
      });
      setSubstituteDialog({
        open: false, medId: null, loading: false, items: [], prescribedMedicine: '', searchTerm: '',
      });
    });
  }

  if (rxLoading || !rx) {
    return (
      <Box sx={fadeInSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <InvButton variant="outlined" tone="teal" size="small" startIcon={<ArrowBackIcon />}
            onClick={() => history.push(backUrl)}>
            Back
          </InvButton>
        </Box>
        {rxLoading ? (
          <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={32} sx={{ color: IPD_COLORS.primary }} />
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>Loading prescription…</Typography>
          </Box>
        ) : (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>{rxError}</Alert>
        )}
      </Box>
    );
  }

  const patient = rx.patient || {};
  const patientName = patient.name || '';
  const ageGender = [patient.age, patient.gender].filter((v) => v != null && v !== '').join(' · ');

  function goBack() {
    if (step === 0) history.push(backUrl);
    else if (!pipeline.state.paymentResult) setStep((s) => s - 1);
    else history.push(backUrl);
  }

  function buildRemarks() {
    const parts = ['OPD prescription dispense'];
    if (rx.doctor_name) parts.push(`Doctor: ${rx.doctor_name}`);
    if (rx.datetime) parts.push(`Rx ${formatDateTime(rx.datetime)}`);
    return parts.join(' · ');
  }

  async function prepareBillOnly() {
    if (!storeId) {
      toast.error('No pharmacy store available');
      throw new Error('No pharmacy store available');
    }
    if (!medicineLinesValid(lines)) {
      toast.error('Add at least one medicine with a valid quantity');
      throw new Error('Add at least one medicine with a valid quantity');
    }
    return pipeline.runPrepareBill({
      lines,
      storeId,
      store,
      patientDisplay: {
        name: patientName,
        mobile: patient.phone_no,
        age: patient.age,
        gender: patient.gender,
      },
      remarks: buildRemarks(),
    });
  }

  async function prepareAndPay(modes) {
    if (!storeId) {
      toast.error('No pharmacy store available');
      return;
    }
    if (!medicineLinesValid(lines)) {
      toast.error('Add at least one medicine with a valid quantity');
      return;
    }

    const prepared = await prepareBillOnly();

    const payable = parseMoney(prepared?.amountPayable ?? pipeline.state.amountPayable);

    const allocated = (modes || []).reduce((sum, m) => sum + parseMoney(m.amount), 0);
    if (Math.abs(allocated - payable) > 0.05) {
      toast.error(
        `Bill priced at ₹ ${payable.toFixed(2)} but payment totals ₹ ${allocated.toFixed(2)}. Adjust payment modes and try again.`,
      );
      return;
    }

    let finalModes = modes;
    if (modes?.length && Math.abs(allocated - payable) > 0.0001) {
      const diff = payable - allocated;
      finalModes = modes.map((m, i) => (
        i === 0
          ? { ...m, amount: String((parseMoney(m.amount) + diff).toFixed(4)) }
          : m
      ));
    }

    await pipeline.collectPayment(finalModes);
  }

  function hardReset() {
    pipeline.resetPipeline();
    setLines([{ ...MEDICINE_LINE_DEFAULT }]);
    setStep(0);
  }

  const prescribedTitle = `Prescribed by ${rx.doctor_name || 'doctor'} · ${formatDateTime(rx.datetime)}`;
  const availabilityEntries = Object.values(availability);
  const availabilityLoading = availabilityEntries.some((a) => a.loading);
  const unresolvedCount = availabilityEntries.filter(
    (a) => !a.loading && a.status && a.status !== STOCK_STATUS.IN_STOCK,
  ).length;

  return (
    <Box sx={fadeInSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InvButton variant="outlined" tone="teal" size="small" startIcon={<ArrowBackIcon />} onClick={goBack}>
          Back
        </InvButton>
      </Box>

      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {STEPS.map((label, i) => (
          <Step
            key={label}
            completed={i < step || !!pipeline.state.paymentResult}
            onClick={() => i < step && !pipeline.state.paymentResult && setStep(i)}
            sx={{ cursor: i < step && !pipeline.state.paymentResult ? 'pointer' : 'default' }}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {storeError && (
        <Alert severity="error" sx={{ borderRadius: '10px', mb: 2.5 }}>
          {storeError}
        </Alert>
      )}

      {step === 0 && (
        <SectionCard title="Prescription" subtitle="Name and mobile feed the bill receipt only — no patient_id is sent">
          <DetailSummaryGrid>
            <Grid item xs={6} sm={3}><SummaryStat label="Patient" value={patientName || '—'} /></Grid>
            <Grid item xs={6} sm={3}><SummaryStat label="Age / Gender" value={ageGender || '—'} /></Grid>
            <Grid item xs={6} sm={3}><SummaryStat label="Phone" value={patient.phone_no || '—'} /></Grid>
            <Grid item xs={6} sm={3}><SummaryStat label="Doctor" value={rx.doctor_name || '—'} /></Grid>
          </DetailSummaryGrid>

          {rx.diagnosis && (
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 1.5 }}>
              <strong>Diagnosis:</strong> {rx.diagnosis}
            </Typography>
          )}

          <PrescribedMedicinesTable
            medications={medications}
            availability={availability}
            onSubstitute={openSubstitute}
          />

          {unresolvedCount > 0 && !availabilityLoading && (
            <Alert severity="warning" sx={{ borderRadius: '10px', mt: 2 }}>
              {unresolvedCount} prescribed medicine{unresolvedCount > 1 ? 's are' : ' is'} unavailable
              at this store. Use Substitute to pick an alternative, or continue and search manually.
            </Alert>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <InvButton disabled={Boolean(storeError) || availabilityLoading} onClick={() => setStep(1)}>
              Continue
            </InvButton>
          </Box>
        </SectionCard>
      )}

      {step === 1 && (
        <>
          <Box sx={{ mb: 2.5 }}>
            <SectionCard title={prescribedTitle} subtitle="In-stock items are already added below — substitute or search for the rest">
              <PrescribedMedicinesTable
                medications={medications}
                availability={availability}
                onSubstitute={openSubstitute}
              />
            </SectionCard>
          </Box>

          <DirectDispenseMedicineStep
            lines={lines}
            setLines={setLines}
            storeId={storeId}
            store={store}
            isSupervisor={can(INV_ACTIONS.CONTROLLED_OVERRIDE)}
            serverTotals={null}
            pipeline={pipeline}
            patientLabel={patientName || 'OPD patient'}
            doctorName={rx.doctor_name}
            patientDisplay={{
              name: patientName,
              mobile: patient.phone_no,
              age: patient.age,
              gender: patient.gender,
            }}
            saleFinalized={pipeline.state.saleFinalized}
            onPrepareBill={prepareBillOnly}
            onPrepareAndPay={prepareAndPay}
            onNewDispense={hardReset}
            saleRemarks={buildRemarks()}
          />
        </>
      )}

      <SubstitutePickerDialog
        open={substituteDialog.open}
        loading={substituteDialog.loading}
        items={substituteDialog.items}
        prescribedMedicine={substituteDialog.prescribedMedicine}
        searchTerm={substituteDialog.searchTerm}
        onSelect={(sub) => applySubstitute(substituteDialog.medId, sub)}
        onClose={() => setSubstituteDialog((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
}
